// libraries/cms-template-components/src/templates/upcoming-trips/index.tsx

"use client";

import type React from "react";
import type { UpcomingTripsDialog } from "../../dialogs/upcoming-trips";

import { UpcomingTrips as UpcomingTripsComponent } from "../../../../components/src/components/upcoming-trips";
import { buildFlightCards } from "../../../../components/src/components/upcoming-trips/features/build-flight-cards";
import { useItineraryStore } from "../../../../components/src/components/upcoming-trips/features/use-itinerary-store";
import { useItineraryView } from "../../../../components/src/components/upcoming-trips/features/use-itinerary-view";
import { setAriaLabelButton } from "../../../../components/src/components/upcoming-trips/services/itinerary-actions";
import { itineraryComponentResolver } from "../../../../components/src/components/upcoming-trips/services/itinerary-content-resolver";
import { normalizeBreadcrumbs } from "../../utils/breadcrumbs.utility";

import type { TemplateComponentProperties } from "@jb-manage-trips/types/magnolia";
import type {
  CardButtonMeta,
  ItineraryContent,
  ItineraryState,
} from "@jb-manage-trips/types/itinerary";

export type UpcomingTripsProperties<T extends string> =
  TemplateComponentProperties<UpcomingTripsDialog<T>>;

function getSabreDxBaseUrl(): string {
  return process.env.NEXT_PUBLIC_SABRE_DX_URL ?? "";
}

function getDestination(item: ItineraryState): string {
  return item.tripSummary.destination || item.tripSummary.destinationCode || "";
}

function getHasSelfService(item: ItineraryState): boolean {
  return Boolean(item.selfService);
}

function getOrigin(item: ItineraryState): string {
  return item.tripSummary.origin || item.tripSummary.originCode || "";
}

export const UpcomingTrips = <T extends string>({
  backgroundImageUrl: _backgroundImageUrl,
  breadcrumbs,
  className = "",
  flag: _flag,
  theme: _theme,
  title,
  ...rest
}: UpcomingTripsProperties<T>) => {
  const { nodes, paths } = normalizeBreadcrumbs(breadcrumbs);

  const { content } = itineraryComponentResolver(rest) as {
    content: ItineraryContent;
  };

  const { data: itinerary } = useItineraryStore();
  const safeItinerary: ItineraryState[] = itinerary ?? [];

  const buttonMeta: CardButtonMeta[] = safeItinerary.map((item) => {
    const destination = getDestination(item);
    const hasSelfService = getHasSelfService(item);
    const origin = getOrigin(item);
    const pnrState = item.pnrState;

    return {
      ariaDisrupted: setAriaLabelButton(
        content,
        origin,
        destination,
        "disrupted",
      ),
      ariaNormal: setAriaLabelButton(content, origin, destination, "normal"),
      disruptedLabel: content.submit.disrupted.label,
      hasSelfService,
      normalLabel: content.submit.normal.label,
      pnrState,
    };
  });

  const { onManageTripClick } = useItineraryView({
    buttonConfig: {
      sabreDxBaseUrl: getSabreDxBaseUrl(),
      supportedLanguages: ["en", "es"],
      useLanguageInUrl: true,
    },
    content,
    itinerary: safeItinerary,
  });

  const flightCards = buildFlightCards({ content, itinerary: safeItinerary });
  const PresentationalComponent = UpcomingTripsComponent as unknown as (
    properties: Record<string, unknown>,
  ) => React.ReactElement | null;

  return (
    <PresentationalComponent
      buttonMeta={buttonMeta}
      className={className}
      flightCards={flightCards}
      nodes={nodes}
      onManageTripClick={(cardIndex: number, isSelfServiceButton: boolean) => {
        const item = safeItinerary[cardIndex];

        if (!item) {
          return;
        }

        onManageTripClick(item, isSelfServiceButton);
      }}
      paths={paths}
      title={content.header || title}
    />
  );
};
